import { useState, useEffect, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { HeartPulse, Clock, Radio, Link, Mail, Bot, Zap, CheckCircle, Inbox, Search, Dna, Hourglass, FileText, BookOpen, ChevronDown, ChevronRight } from 'lucide-react';
import { getMasterMemory, deleteMasterMemoryEntry, clearMasterMemory, getWaitingConversations, deleteWaitingConversation, clearWaitingConversations, getThreadMemoryMd } from '../data';
import './AgentPages.css';

const EVENT_TYPE_CONFIG = {
  heartbeat_fired: { label: 'Heartbeat', color: '#ec4899', Icon: HeartPulse },
  cron_fired: { label: 'Cron Job', color: '#a855f7', Icon: Clock },
  poll_processed: { label: 'Poll', color: '#3b82f6', Icon: Radio },
  webhook_processed: { label: 'Webhook', color: '#14b8a6', Icon: Link },
  webhook_push_processed: { label: 'Webhook Push', color: '#06b6d4', Icon: Mail },
  agent_action: { label: 'Agent Action', color: '#f59e0b', Icon: Bot },
  integration_call: { label: 'Integration', color: '#10b981', Icon: Zap },
  end_interaction: { label: 'Completed', color: '#059669', Icon: CheckCircle },
  event_received: { label: 'Event Received', color: '#6366f1', Icon: Inbox },
  memory_md_read: { label: 'Memory Read', color: '#22d3ee', Icon: BookOpen },
  memory_md_write: { label: 'Memory Write', color: '#22d3ee', Icon: FileText },
};

function getEventConfig(eventType) {
  return EVENT_TYPE_CONFIG[eventType] || { label: eventType || 'Unknown', color: '#64748b' };
}

// "expecting" is one of: a free-text description (actively waiting), or one of
// the end tokens END_TURN / END_CONVERSATION (turn finished). Legacy "END" is
// still treated as a closed turn for older entries.
const END_TOKENS = new Set(['END', 'END_TURN', 'END_CONVERSATION']);
function isAwaitingReply(expecting) {
  return !!expecting && !END_TOKENS.has(String(expecting).toUpperCase());
}

function ThreadMemoryMdPanel({ agentId, threadId }) {
  const [state, setState] = useState({ loading: true, content: '', size: 0, error: null });
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!agentId || !threadId || threadId === '_no_thread') {
      setState({ loading: false, content: '', size: 0, error: null });
      return () => { alive = false; };
    }
    setState({ loading: true, content: '', size: 0, error: null });
    getThreadMemoryMd(agentId, threadId)
      .then((res) => {
        if (!alive) return;
        setState({ loading: false, content: res?.content || '', size: res?.size_bytes || 0, error: null });
      })
      .catch((err) => {
        if (!alive) return;
        setState({ loading: false, content: '', size: 0, error: err?.message || 'Failed to load memory.md' });
      });
    return () => { alive = false; };
  }, [agentId, threadId]);

  if (!threadId || threadId === '_no_thread') return null;

  const hasContent = !!state.content.trim();

  return (
    <div className="mm-memory-md-panel" style={{
      margin: '12px 0 4px',
      background: '#1a1d24',
      border: '1px solid #2a2f3a',
      borderRadius: 8,
      overflow: 'hidden',
    }}>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '10px 14px',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          color: 'inherit',
          textAlign: 'left',
        }}
      >
        {expanded ? <ChevronDown size={14} style={{ color: '#94a3b8' }} /> : <ChevronRight size={14} style={{ color: '#94a3b8' }} />}
        <FileText size={14} style={{ color: '#94a3b8' }} />
        <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: 0.4, color: '#cbd5e1', textTransform: 'uppercase' }}>
          memory.md
        </span>
        <span style={{ fontSize: 11, color: '#64748b' }}>
          per-thread agent scratch file{state.size ? ` · ${state.size} bytes` : ''}
        </span>
        {!expanded && !state.loading && (
          <span style={{ marginLeft: 'auto', fontSize: 11, color: '#475569', fontStyle: hasContent ? 'normal' : 'italic' }}>
            {hasContent ? 'click to view' : 'empty'}
          </span>
        )}
      </button>
      {expanded && (
        <div style={{ padding: '0 14px 12px' }}>
          {state.loading ? (
            <div style={{ fontSize: 12, color: '#64748b', fontStyle: 'italic' }}>Loading…</div>
          ) : state.error ? (
            <div style={{ fontSize: 12, color: '#dc2626' }}>{state.error}</div>
          ) : hasContent ? (
            <pre style={{
              margin: 0,
              padding: '10px 12px',
              background: '#0f1218',
              border: '1px solid #2a2f3a',
              borderRadius: 6,
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontSize: 12,
              color: '#e2e8f0',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              maxHeight: 320,
              overflowY: 'auto',
            }}>{state.content}</pre>
          ) : (
            <div style={{ fontSize: 12, color: '#64748b', fontStyle: 'italic' }}>
              Empty. The agent hasn't written anything to this thread's memory.md yet — it can do so via the <code>write_memory_md</code> action.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function formatTimestamp(ts) {
  if (!ts) return '';
  try {
    const date = new Date(ts);
    if (isNaN(date.getTime())) return ts;
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHrs = Math.floor(diffMs / 3600000);

    let relative;
    if (diffMins < 1) relative = 'just now';
    else if (diffMins < 60) relative = `${diffMins}m ago`;
    else if (diffHrs < 24) relative = `${diffHrs}h ago`;
    else relative = `${Math.floor(diffHrs / 24)}d ago`;

    const timeStr = date.toLocaleString('en-US', {
      month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
      hour12: true
    });

    return `${timeStr} (${relative})`;
  } catch {
    return ts;
  }
}

function sameLocalDay(a, b) {
  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();
}

function formatThreadRange(earliestMs, latestMs) {
  if (!latestMs) return '';
  const latest = new Date(latestMs);
  if (!earliestMs || earliestMs === latestMs || !isFinite(earliestMs)) {
    return formatTimestamp(latest.toISOString());
  }
  const earliest = new Date(earliestMs);
  const dateOpts = { month: 'short', day: 'numeric' };
  const timeOpts = { hour: '2-digit', minute: '2-digit', hour12: true };
  if (sameLocalDay(earliest, latest)) {
    return `${latest.toLocaleDateString('en-US', dateOpts)} · ${earliest.toLocaleTimeString('en-US', timeOpts)} – ${latest.toLocaleTimeString('en-US', timeOpts)}`;
  }
  return `${earliest.toLocaleDateString('en-US', dateOpts)} → ${latest.toLocaleDateString('en-US', dateOpts)}`;
}

function shortThreadId(tid) {
  if (!tid) return '—';
  if (tid.length > 16) return tid.substring(0, 12) + '…';
  return tid;
}

function EntryCard({ entry, isExpanded, onToggle, onDelete }) {
  const cfg = getEventConfig(entry.event_type);
  return (
    <div className={`mm-entry-card ${isExpanded ? 'expanded' : ''}`}>
      <div className="mm-entry-header" onClick={onToggle}>
        <div className="mm-entry-type" style={{ backgroundColor: `${cfg.color}20`, color: cfg.color, borderColor: `${cfg.color}40` }}>
          {cfg.Icon && <cfg.Icon size={14} />}
          <span className="mm-type-label">{cfg.label}</span>
        </div>

        <div className="mm-entry-content">
          <div className="mm-entry-summary">{entry.summary || 'No summary'}</div>
          <div className="mm-entry-meta">
            <span className="mm-meta-source">{entry.source}</span>
            <span className="mm-meta-dot">·</span>
            <span className="mm-meta-time">{formatTimestamp(entry.timestamp)}</span>
            {entry.outcome && (
              <>
                <span className="mm-meta-dot">·</span>
                <span className="mm-meta-outcome">{entry.outcome}</span>
              </>
            )}
          </div>
        </div>

        {isAwaitingReply(entry.expecting) && (
          <div className="mm-expecting-badge">Expecting</div>
        )}

        <span className="mm-expand-icon">{isExpanded ? '−' : '+'}</span>
      </div>

      {isExpanded && (
        <motion.div
          className="mm-entry-details"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
        >
          <div className="mm-detail-grid">
            <div className="mm-detail-item">
              <span className="mm-detail-label">Event Type</span>
              <span className="mm-detail-value">{entry.event_type}</span>
            </div>
            <div className="mm-detail-item">
              <span className="mm-detail-label">Source</span>
              <span className="mm-detail-value">{entry.source || '—'}</span>
            </div>
            <div className="mm-detail-item">
              <span className="mm-detail-label">Thread ID</span>
              <span className="mm-detail-value mm-mono">{entry.thread_id || '—'}</span>
            </div>
            <div className="mm-detail-item">
              <span className="mm-detail-label">Timestamp</span>
              <span className="mm-detail-value">{entry.timestamp || '—'}</span>
            </div>
            {entry.date && (
              <div className="mm-detail-item">
                <span className="mm-detail-label">Date</span>
                <span className="mm-detail-value">{entry.date}</span>
              </div>
            )}
            {entry.outcome && (
              <div className="mm-detail-item">
                <span className="mm-detail-label">Outcome</span>
                <span className="mm-detail-value">{entry.outcome}</span>
              </div>
            )}
            {entry.integration && (
              <div className="mm-detail-item">
                <span className="mm-detail-label">Integration</span>
                <span className="mm-detail-value">{entry.integration}</span>
              </div>
            )}
          </div>

          {entry.metadata && Object.keys(entry.metadata).length > 0 && (
            <div style={{ marginTop: '12px' }}>
              <span className="mm-detail-label">Metadata</span>
              <div className="mm-detail-grid" style={{ marginTop: '6px' }}>
                {Object.entries(entry.metadata).map(([key, value]) => (
                  <div className="mm-detail-item" key={key}>
                    <span className="mm-detail-label">{key}</span>
                    <span className="mm-detail-value mm-mono">{typeof value === 'object' ? JSON.stringify(value) : String(value)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {entry.expectation_parameters && Object.keys(entry.expectation_parameters).length > 0 && (
            <div style={{ marginTop: '12px' }}>
              <span className="mm-detail-label">Expectation Parameters</span>
              <div className="mm-detail-grid" style={{ marginTop: '6px' }}>
                {Object.entries(entry.expectation_parameters).map(([key, value]) => (
                  <div className="mm-detail-item" key={key}>
                    <span className="mm-detail-label">{key}</span>
                    <span className="mm-detail-value mm-mono">{typeof value === 'object' ? JSON.stringify(value) : String(value)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {isAwaitingReply(entry.expecting) && (
            <div className="mm-expecting-section">
              <span className="mm-detail-label">Expecting</span>
              <div className="mm-expecting-text">{entry.expecting}</div>
            </div>
          )}

          {entry.summary && (
            <div className="mm-summary-section">
              <span className="mm-detail-label">Full Summary</span>
              <div className="mm-summary-text">{entry.summary}</div>
            </div>
          )}

          <div className="mm-entry-actions">
            <button
              className="btn btn-sm btn-outline"
              onClick={(e) => { e.stopPropagation(); onDelete(entry._id); }}
              style={{ color: '#dc2626', borderColor: '#fecaca' }}
            >
              Delete
            </button>
          </div>
        </motion.div>
      )}
    </div>
  );
}

export default function AgentMasterMemory() {
  const { agent, agentId } = useOutletContext();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [clearing, setClearing] = useState(false);
  const [viewMode, setViewMode] = useState('grouped');
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [waitingEntries, setWaitingEntries] = useState([]);
  const [loadingWaiting, setLoadingWaiting] = useState(false);
  const [waitingLoaded, setWaitingLoaded] = useState(false);
  const [clearingWaiting, setClearingWaiting] = useState(false);

  const loadWaiting = async () => {
    try {
      setLoadingWaiting(true);
      const data = await getWaitingConversations(agentId);
      setWaitingEntries(data || []);
      setWaitingLoaded(true);
    } catch (err) {
      console.error('Failed to load waiting entries:', err);
    } finally {
      setLoadingWaiting(false);
    }
  };

  const handleDeleteWaiting = async (entryId) => {
    try {
      await deleteWaitingConversation(agentId, entryId);
      setWaitingEntries(waitingEntries.filter(e => (e._id || e.id) !== entryId));
    } catch (err) {
      console.error('Failed to delete waiting entry:', err);
    }
  };

  const handleClearAllWaiting = async () => {
    if (!confirm('Clear all waiting entries? This cannot be undone.')) return;
    try {
      setClearingWaiting(true);
      await clearWaitingConversations(agentId);
      setWaitingEntries([]);
    } catch (err) {
      console.error('Failed to clear waiting entries:', err);
    } finally {
      setClearingWaiting(false);
    }
  };

  useEffect(() => {
    if (viewMode === 'waiting' && !waitingLoaded) {
      loadWaiting();
    }
  }, [viewMode]);

  useEffect(() => {
    loadMasterMemory();
  }, [agentId]);

  useEffect(() => {
    if (viewMode === 'waiting') {
      loadWaiting();
    }
  }, [agentId]);

  const loadMasterMemory = async () => {
    try {
      setLoading(true);
      const data = await getMasterMemory(agentId);
      const sorted = (data || []).sort((a, b) => {
        const dateA = new Date(a.timestamp || 0);
        const dateB = new Date(b.timestamp || 0);
        return dateB - dateA;
      });
      setEntries(sorted);
    } catch (err) {
      console.error('Failed to load master memory:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (entryId) => {
    try {
      await deleteMasterMemoryEntry(agentId, entryId);
      setEntries(entries.filter(e => e._id !== entryId));
    } catch (err) {
      console.error('Failed to delete entry:', err);
    }
  };

  const handleClearAll = async () => {
    if (!confirm('Clear all master memory entries? This cannot be undone.')) return;
    try {
      setClearing(true);
      await clearMasterMemory(agentId);
      setEntries([]);
    } catch (err) {
      console.error('Failed to clear master memory:', err);
    } finally {
      setClearing(false);
    }
  };

  const eventTypes = [...new Set(entries.map(e => e.event_type))];

  const filteredEntries = entries.filter(entry => {
    const matchesFilter = filter === 'all' || entry.event_type === filter;
    const matchesSearch = !searchQuery ||
      entry.summary?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      entry.source?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      entry.outcome?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      entry.expecting?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      entry.thread_id?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const threadGroups = useMemo(() => {
    const groups = {};
    filteredEntries.forEach(entry => {
      const tid = entry.thread_id || '_no_thread';
      if (!groups[tid]) {
        groups[tid] = {
          thread_id: tid,
          entries: [],
          latestMs: 0,
          earliestMs: Infinity,
          sources: new Set(),
          eventTypes: new Set(),
          hasExpecting: false,
        };
      }
      groups[tid].entries.push(entry);
      groups[tid].sources.add(entry.source || 'unknown');
      groups[tid].eventTypes.add(entry.event_type);
      if (isAwaitingReply(entry.expecting)) {
        groups[tid].hasExpecting = true;
      }
      try {
        const ts = new Date(entry.timestamp || 0).getTime();
        if (!isNaN(ts) && ts > 0) {
          if (ts > groups[tid].latestMs) groups[tid].latestMs = ts;
          if (ts < groups[tid].earliestMs) groups[tid].earliestMs = ts;
        }
      } catch {}
    });

    return Object.values(groups)
      .map(g => ({
        ...g,
        sources: Array.from(g.sources),
        eventTypes: Array.from(g.eventTypes),
      }))
      .sort((a, b) => b.latestMs - a.latestMs);
  }, [filteredEntries]);

  const toggleGroup = (threadId) => {
    setCollapsedGroups(prev => ({ ...prev, [threadId]: !prev[threadId] }));
  };

  const sourceGroups = filteredEntries.reduce((groups, entry) => {
    const source = entry.source || 'unknown';
    if (!groups[source]) groups[source] = [];
    groups[source].push(entry);
    return groups;
  }, {});

  return (
    <div className="config-page">
      <div className="config-header">
        <h1>Master Memory</h1>
        <p>Global activity log across all listeners, skill invocations, and sub-agents. This is what your agent uses to avoid duplicate work and maintain continuity across conversations.</p>
      </div>

      <div className="config-section">
        <div className="section-header">
          <div className="section-icon"><Search size={18} /></div>
          <div className="section-text">
            <h2>Search & Filter</h2>
            <p>Filter by event type or search across all entries</p>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group" style={{ flex: 1 }}>
            <input
              type="text"
              className="input"
              placeholder="Search summaries, sources, thread IDs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        <div className="mm-filters">
          <button
            className={`mm-filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All ({entries.length})
          </button>
          {eventTypes.map(type => {
            const cfg = getEventConfig(type);
            const count = entries.filter(e => e.event_type === type).length;
            return (
              <button
                key={type}
                className={`mm-filter-btn ${filter === type ? 'active' : ''}`}
                onClick={() => setFilter(type)}
                style={filter === type ? { borderColor: cfg.color, color: cfg.color } : {}}
              >
                {cfg.label} ({count})
              </button>
            );
          })}
        </div>
      </div>

      <div className="config-section">
        <div className="section-header">
          <div className="section-icon"><Dna size={18} /></div>
          <div className="section-text">
            <h2>Activity Log</h2>
            <p>{viewMode === 'waiting' ? `${waitingEntries.length} waiting conversation(s)` : `${filteredEntries.length} entries ${viewMode === 'grouped' ? `in ${threadGroups.length} thread(s)` : ''} ${filter !== 'all' ? `matching "${getEventConfig(filter).label}"` : ''}`}</p>
          </div>
          <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto', alignItems: 'center' }}>
            <div className="mm-view-toggle">
              <button
                className={`mm-view-btn ${viewMode === 'grouped' ? 'active' : ''}`}
                onClick={() => setViewMode('grouped')}
              >
                Grouped
              </button>
              <button
                className={`mm-view-btn ${viewMode === 'timeline' ? 'active' : ''}`}
                onClick={() => setViewMode('timeline')}
              >
                Timeline
              </button>
              <button
                className={`mm-view-btn ${viewMode === 'waiting' ? 'active' : ''}`}
                onClick={() => setViewMode('waiting')}
                style={viewMode === 'waiting' ? { borderColor: '#f59e0b', color: '#f59e0b' } : {}}
              >
                <Hourglass size={14} /> Waiting {waitingLoaded && waitingEntries.length > 0 ? `(${waitingEntries.length})` : ''}
              </button>
            </div>
            {viewMode !== 'waiting' && entries.length > 0 && (
              <button
                className="btn btn-sm btn-outline"
                onClick={handleClearAll}
                disabled={clearing}
                style={{ color: '#dc2626', borderColor: '#fecaca' }}
              >
                {clearing ? 'Clearing...' : 'Clear All'}
              </button>
            )}
          </div>
        </div>

        {viewMode === 'waiting' ? (
          loadingWaiting ? (
            <div className="empty-state">
              <div className="empty-state-icon"><Hourglass size={24} /></div>
              <h3>Loading waiting conversations...</h3>
            </div>
          ) : waitingEntries.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon"><Hourglass size={24} /></div>
              <h3>No waiting conversations</h3>
              <p>Conversations waiting for follow-up input will appear here.</p>
            </div>
          ) : (
            <div className="mm-entries-list">
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
                <button
                  className="btn btn-sm btn-outline"
                  onClick={handleClearAllWaiting}
                  disabled={clearingWaiting}
                  style={{ color: '#dc2626', borderColor: '#fecaca' }}
                >
                  {clearingWaiting ? 'Clearing...' : 'Clear All Waiting'}
                </button>
              </div>
              <AnimatePresence>
                {waitingEntries.map((entry, index) => {
                  const entryId = entry._id || entry.id;
                  return (
                    <motion.div
                      key={entryId || index}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: Math.min(index * 0.03, 0.5) }}
                    >
                      <div className="mm-entry-card" style={{ borderLeft: '3px solid #f59e0b' }}>
                        <div className="mm-entry-header" onClick={() => setExpandedId(expandedId === entryId ? null : entryId)}>
                          <div className="mm-entry-type" style={{ backgroundColor: 'rgba(245, 158, 11, 0.12)', color: '#f59e0b', borderColor: 'rgba(245, 158, 11, 0.3)' }}>
                            <span className="mm-type-icon"><Hourglass size={12} /></span>
                            <span className="mm-type-label">Waiting</span>
                          </div>
                          <div className="mm-entry-content">
                            <div className="mm-entry-summary" style={{ color: '#fbbf24', fontWeight: 600 }}>
                              {entry.expecting || 'Waiting for input'}
                            </div>
                            <div className="mm-entry-meta">
                              <span className="mm-meta-source">{entry.source || entry.listener_name || '—'}</span>
                              <span className="mm-meta-dot">·</span>
                              <span className="mm-meta-time">{formatTimestamp(entry.created_at || entry.timestamp)}</span>
                            </div>
                          </div>
                          <span className="mm-expand-icon">{expandedId === entryId ? '−' : '+'}</span>
                        </div>

                        {expandedId === entryId && (
                          <motion.div
                            className="mm-entry-details"
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                          >
                            <div className="mm-detail-grid">
                              <div className="mm-detail-item">
                                <span className="mm-detail-label">Thread ID</span>
                                <span className="mm-detail-value mm-mono">{entry.thread_id || '—'}</span>
                              </div>
                              <div className="mm-detail-item">
                                <span className="mm-detail-label">Source</span>
                                <span className="mm-detail-value">{entry.source || entry.listener_name || '—'}</span>
                              </div>
                              {entry.goal && (
                                <div className="mm-detail-item">
                                  <span className="mm-detail-label">Goal</span>
                                  <span className="mm-detail-value">{entry.goal}</span>
                                </div>
                              )}
                              <div className="mm-detail-item">
                                <span className="mm-detail-label">Created</span>
                                <span className="mm-detail-value">{formatTimestamp(entry.created_at || entry.timestamp)}</span>
                              </div>
                            </div>

                            <div style={{ marginTop: '12px', padding: '12px', background: 'rgba(245, 158, 11, 0.08)', borderRadius: '8px', border: '1px solid rgba(245, 158, 11, 0.2)' }}>
                              <span className="mm-detail-label" style={{ color: '#f59e0b' }}>Expecting</span>
                              <div style={{ marginTop: '6px', fontSize: '14px', color: '#fbbf24', fontWeight: 500, lineHeight: 1.5 }}>
                                {entry.expecting || '—'}
                              </div>
                            </div>

                            {entry.expectation_parameters && Object.keys(entry.expectation_parameters).length > 0 && (
                              <div style={{ marginTop: '12px' }}>
                                <span className="mm-detail-label">Expectation Parameters</span>
                                <div className="mm-detail-grid" style={{ marginTop: '6px' }}>
                                  {Object.entries(entry.expectation_parameters).map(([key, value]) => (
                                    <div className="mm-detail-item" key={key}>
                                      <span className="mm-detail-label">{key}</span>
                                      <span className="mm-detail-value">{typeof value === 'object' ? JSON.stringify(value) : String(value)}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {entry.metadata && Object.keys(entry.metadata).length > 0 && (
                              <div style={{ marginTop: '12px' }}>
                                <span className="mm-detail-label">Metadata</span>
                                <div className="mm-detail-grid" style={{ marginTop: '6px' }}>
                                  {Object.entries(entry.metadata).map(([key, value]) => (
                                    <div className="mm-detail-item" key={key}>
                                      <span className="mm-detail-label">{key}</span>
                                      <span className="mm-detail-value">{typeof value === 'object' ? JSON.stringify(value) : String(value)}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {entry.summary && (
                              <div className="mm-summary-section">
                                <span className="mm-detail-label">Summary</span>
                                <div className="mm-summary-text">{entry.summary}</div>
                              </div>
                            )}

                            <div className="mm-entry-actions">
                              <button
                                className="btn btn-sm btn-outline"
                                onClick={(e) => { e.stopPropagation(); handleDeleteWaiting(entryId); }}
                                style={{ color: '#dc2626', borderColor: '#fecaca' }}
                              >
                                Delete
                              </button>
                            </div>
                          </motion.div>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          )
        ) : loading ? (
          <div className="empty-state">
            <div className="empty-state-icon"><Hourglass size={24} /></div>
            <h3>Loading master memory...</h3>
          </div>
        ) : filteredEntries.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon"><Dna size={24} /></div>
            <h3>No entries yet</h3>
            <p>Activity will appear here as your agent processes events, invokes skills, and completes tasks.</p>
          </div>
        ) : viewMode === 'grouped' ? (
          <div className="mm-thread-groups">
            {threadGroups.map((group, gIdx) => {
              const isCollapsed = collapsedGroups[group.thread_id] ?? false;
              const lastEntry = group.entries[0];

              return (
                <motion.div
                  key={group.thread_id}
                  className="mm-thread-group"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(gIdx * 0.03, 0.5) }}
                >
                  <div
                    className="mm-thread-header"
                    onClick={() => toggleGroup(group.thread_id)}
                  >
                    <div className="mm-thread-left">
                      <div className="mm-thread-icons">
                        {group.eventTypes.slice(0, 4).map((et, i) => {
                          const c = getEventConfig(et);
                          return c.Icon ? <c.Icon key={i} size={12} title={c.label} style={{ color: c.color }} /> : <span key={i} title={c.label} style={{ color: c.color, fontSize: '10px', fontWeight: 600 }}>{c.label.substring(0, 2).toUpperCase()}</span>;
                        })}
                      </div>
                      <div className="mm-thread-info">
                        <div className="mm-thread-title">
                          {lastEntry?.summary?.substring(0, 80) || `Thread ${shortThreadId(group.thread_id)}`}
                        </div>
                        <div className="mm-thread-meta">
                          <span className="mm-mono mm-thread-id-label">{shortThreadId(group.thread_id)}</span>
                          <span className="mm-meta-dot">·</span>
                          <span>{group.sources.join(', ')}</span>
                          <span className="mm-meta-dot">·</span>
                          <span>{formatThreadRange(group.earliestMs, group.latestMs)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="mm-thread-right">
                      {group.hasExpecting && (
                        <div className="mm-expecting-badge">Expecting</div>
                      )}
                      <span className="mm-thread-count">{group.entries.length}</span>
                      <span className="mm-expand-icon">{isCollapsed ? '+' : '−'}</span>
                    </div>
                  </div>

                  {!isCollapsed && (
                    <div className="mm-thread-entries">
                      <ThreadMemoryMdPanel agentId={agentId} threadId={group.thread_id} />
                      <div className="mm-thread-timeline">
                        {group.entries.map((entry, eIdx) => (
                          <EntryCard
                            key={entry._id || eIdx}
                            entry={entry}
                            isExpanded={expandedId === entry._id}
                            onToggle={() => setExpandedId(expandedId === entry._id ? null : entry._id)}
                            onDelete={handleDelete}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        ) : (
          <div className="mm-entries-list">
            <AnimatePresence>
              {filteredEntries.map((entry, index) => (
                <motion.div
                  key={entry._id || index}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(index * 0.02, 0.5) }}
                >
                  <EntryCard
                    entry={entry}
                    isExpanded={expandedId === entry._id}
                    onToggle={() => setExpandedId(expandedId === entry._id ? null : entry._id)}
                    onDelete={handleDelete}
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}

        {viewMode !== 'waiting' && Object.keys(sourceGroups).length > 1 && filteredEntries.length > 0 && (
          <div className="mm-source-summary">
            <h4>Activity by Source</h4>
            <div className="mm-source-bars">
              {Object.entries(sourceGroups)
                .sort((a, b) => b[1].length - a[1].length)
                .map(([source, items]) => (
                  <div key={source} className="mm-source-bar-row">
                    <span className="mm-source-name">{source}</span>
                    <div className="mm-source-bar-track">
                      <div
                        className="mm-source-bar-fill"
                        style={{ width: `${(items.length / filteredEntries.length) * 100}%` }}
                      />
                    </div>
                    <span className="mm-source-count">{items.length}</span>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
